import { useState, type ReactNode } from 'react';
import { Btn, Card, Field, SectionHead } from './ui/primitives';
import type { Member, ReportStatus } from '../lib/contract';

/**
 * The shared batch REPORT-CONTENT editor (user request 2026-07-22, reworked 2026-07-23).
 *
 * The real objective: a user with 100 open KEVs, 90 of them the same defect (e.g. every
 * FreeRTOS-Plus-TCP unit), writes ONE report and applies it to all 90. So this editor
 * exposes the SAME free-text fields a single report does — above all "General
 * information" (Q16 v15/i14), the field that actually matters at the 24h/72h stage and
 * that the first version wrongly omitted. Each field maps to a Q16 override server-side,
 * so a batch edit and a single edit fill the exact same store.
 *
 * Reused by the incidents list, a product's Reports tab, and a version's Findings table.
 */

export interface BatchContentPatch {
  generalInfo?: string;
  nature?: string;
  measuresTaken?: string;
  measures?: string;
  impact?: string;
  assigneeUserRef?: string;
  reportStatus?: string;
  stage?: string;
}

const REPORT_STATUSES: ReportStatus[] = ['New', 'Triaged', 'Pending review', 'Reviewed', 'Sent'];
const STAGES = ['24h', '72h', '14d', '1m', 'complete'] as const;

interface Props {
  /** How many reports the content will be written to. */
  count: number;
  busy: boolean;
  members: Member[];
  onApply: (patch: BatchContentPatch) => void;
  onCancel: () => void;
  /** Extra context line under the header (e.g. "applies to the N KEV findings"). */
  note?: ReactNode;
  /** Show the owner / status / stage selects (default true). */
  showMeta?: boolean;
}

export function BatchContentEditor({ count, busy, members, onApply, onCancel, note, showMeta = true }: Props) {
  const [generalInfo, setGeneralInfo] = useState('');
  const [nature, setNature] = useState('');
  const [measuresTaken, setMeasuresTaken] = useState('');
  const [measures, setMeasures] = useState('');
  const [impact, setImpact] = useState('');
  const [assignee, setAssignee] = useState('');
  const [reportStatus, setReportStatus] = useState('');
  const [stage, setStage] = useState('');

  function apply() {
    const patch: BatchContentPatch = {};
    if (generalInfo.trim()) patch.generalInfo = generalInfo.trim();
    if (nature.trim()) patch.nature = nature.trim();
    if (measuresTaken.trim()) patch.measuresTaken = measuresTaken.trim();
    if (measures.trim()) patch.measures = measures.trim();
    if (impact.trim()) patch.impact = impact.trim();
    if (assignee) patch.assigneeUserRef = assignee;
    if (reportStatus) patch.reportStatus = reportStatus;
    if (stage) patch.stage = stage;
    onApply(patch);
  }

  return (
    <Card data-testid="batch-editor" style={{ padding: 16, marginTop: 8 }}>
      <SectionHead
        title={`Write one report, apply to ${count}`}
        aside={<span className="hint">only the fields you fill are written to all {count}</span>}
      />
      <p className="hint" style={{ margin: '6px 0 14px' }}>
        These are the same fields a single report has. Fill them once — e.g. every FreeRTOS-Plus-TCP KEV,
        same root and same fix — and each of the {count} selected reports gets them. {note}
      </p>
      <div className="form" style={{ display: 'grid', gap: 12, maxWidth: 820 }}>
        {/* General information FIRST — the field ENISA shows first and the one that matters at 24h/72h. */}
        <Field
          label="General information"
          hint="what the vulnerability is · the main field on the ENISA early warning (recommended at 24h, mandatory at 72h)"
        >
          <textarea
            className="input" rows={3} data-testid="batch-generalinfo" value={generalInfo}
            onChange={(e) => setGeneralInfo(e.target.value)}
            placeholder="e.g. Out-of-bounds write in the FreeRTOS-Plus-TCP stack, reachable from unauthenticated IPv6 traffic; listed in CISA KEV as actively exploited."
          />
        </Field>
        <Field label="General nature / root cause" hint="Q16 v16 · e.g. memory corruption, auth bypass — the shared defect class">
          <textarea
            className="input" rows={2} data-testid="batch-nature" value={nature}
            onChange={(e) => setNature(e.target.value)}
            placeholder="e.g. Out-of-bounds write (memory corruption) in the TCP/IP stack."
          />
        </Field>
        <Field label="Corrective measures you have taken" hint="Q16 v18 · the patch or mitigation you shipped">
          <textarea
            className="input" rows={2} data-testid="batch-measures-taken" value={measuresTaken}
            onChange={(e) => setMeasuresTaken(e.target.value)}
            placeholder="e.g. Fixed in firmware 4.0.1, pushed to all fielded units via OTA."
          />
        </Field>
        <Field label="Measures users can take (the fix)" hint="Q16 v19 · workarounds, configuration, monitoring">
          <textarea
            className="input" rows={2} data-testid="batch-measures" value={measures}
            onChange={(e) => setMeasures(e.target.value)}
            placeholder="e.g. Update FreeRTOS-Plus-TCP to ≥ 4.0.0, or apply the backported patch; restrict the affected port meanwhile."
          />
        </Field>
        <Field label="Impact" hint="Q16 v24 · affected functionality / data exposure">
          <textarea
            className="input" rows={2} data-testid="batch-impact" value={impact}
            onChange={(e) => setImpact(e.target.value)}
            placeholder="e.g. Remote code execution with device privileges; full loss of confidentiality and integrity."
          />
        </Field>
        {showMeta && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="hint">Also set (optional):</span>
            <select className="select" style={{ width: 'auto', padding: '5px 8px' }} value={assignee} data-testid="batch-assignee" onChange={(e) => setAssignee(e.target.value)}>
              <option value="">— keep owner —</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select className="select" style={{ width: 'auto', padding: '5px 8px' }} value={reportStatus} data-testid="batch-status" onChange={(e) => setReportStatus(e.target.value)}>
              <option value="">— keep status —</option>
              {REPORT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="select" style={{ width: 'auto', padding: '5px 8px' }} value={stage} data-testid="batch-stage" onChange={(e) => setStage(e.target.value)}>
              <option value="">— keep stage —</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="form-actions" style={{ display: 'flex', gap: 8 }}>
          <Btn variant="primary" disabled={busy} data-testid="batch-apply" onClick={apply}>{busy ? 'Applying…' : `Apply to ${count}`}</Btn>
          <Btn disabled={busy} onClick={onCancel}>Cancel</Btn>
        </div>
      </div>
    </Card>
  );
}
